/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Grid, Typography, Button, Box, Slide, useTheme } from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash';
import ReactMuiTableColumnHeaderTextEllipsis from '../../components/Common/ReactMuiTableColumnHeaderTextEllipsis';
import ReactMuiTableListView from '../../components/Common/TableComponent/TableComponent';
import AddSubadminTabDialog from '../../components/SubadminTabs/AddSubadminTabDialog';
import useSubadmin from '../../hooks/useSubadmin';
import { ReactComponent as NoDataImage } from '../../assets/NoDataImage.svg';
import { ReactComponent as NoDataCreated } from '../../assets/NoDataCreated.svg';
import DeleteDialog from '../../components/Common/DeleteDialog';
import utils from '../../utils/Utils';
import SearchBox from '../../components/Common/SearchBox';
import ChangeStatusDialog from '../../components/Common/ChangeStatusDialog';
import {
  CommonTableCell,
  StatusCell,
} from '../../components/Common/CommonTableCell';

/**
 * @function Subadmin
 *
 * @description The Subadmin
 *  component displays a list of applications with options to search, add, edit, delete, and change the status of the applications.
 * @returns {JSX} renders Subadmin
 *  component UI
 * @example <Subadmin
 * />
 */

const Subadmin = () => {
  const theme = useTheme();
  const orgName = useSelector((state) => state.common.orgName);
  const { getUserList, deteleSubadmin, updateSubadmin } = useSubadmin();

  const [userList, setUserList] = useState([]);
  const [deleteUser, setDeleteUser] = useState({});
  const [editUser, setEditUser] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [open, setOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mode, setMode] = useState('create');
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQueryText, setSearchQueryText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showStatusChangeModal, setShowStatusChangeModal] = useState(false);

  const tableColumns = useMemo(
    () => [
      {
        heading: 'Full Name',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'full_name',
        sortType: utils.caseInsensitiveSort,
        Cell: CommonTableCell,
        width: 150,
      },
      {
        heading: 'Email ID',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'email',
        Cell: CommonTableCell,
        width: 150,
      },
      {
        heading: 'Organization',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'display_name',
        Cell: CommonTableCell,
        width: 150,
      },
      {
        heading: 'Status',
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        accessor: 'is_active',
        Cell: StatusCell,
        sortType: utils.statusSort,
        width: 150,
      },
    ],
    [utils]
  );

  useEffect(() => {
    setIsLoading(true);
    getUserList(pageIndex, pageSize, searchTerm).then((resp) => {
      setUserList(resp?.data);
      setPageCount(resp?.max_pages);
      setTotalCount(resp?.total_count);
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (pageIndex >= 0 && pageSize > 0) {
      getUserList(pageIndex, pageSize, searchTerm).then((resp) => {
        setUserList(resp?.data);
        setPageCount(resp?.max_pages);
        setTotalCount(resp?.total_count);
      });
    }
  }, [pageIndex, pageSize]);

  useEffect(() => {
    getUserList(0, pageSize, searchTerm).then((resp) => {
      setUserList(resp?.data);
      setPageCount(resp?.max_pages);
      setTotalCount(resp?.total_count);
    });
  }, [searchTerm]);

  /**
   * @function debouncedSave
   * @description debounce function to update the search term
   * @returns {void} sets the the searchTerm value
   * @example debouncedSave()
   */
  const debouncedSave = useCallback(
    debounce((data) => {
      setSearchTerm(data);
    }, 800),
    []
  );

  /**
   * @function handleSearchUserByName
   * @description handles the search input change to filter applications by name.
   * @param {object} e - The event object from the search input field.
   * @returns {void} sets the the searchTerm value
   * @example handleSearchUserByName(event)
   */

  const handleSearchUserByName = (e) => {
    debouncedSave(e.target.value); // Call the debounced function
  };

  /**
   * @function handleSetSearchText
   * @description handles the input field of searchText.
   * @param {object} searchText - the current text is passed from user for searching.
   * @returns {void} sets the searchText value
   * @example handleSetSearchText(searchText)
   */
  const handleSetSearchText = useCallback((searchText) => {
    setSearchQueryText(searchText);
  }, []);

  /**
   * @function handleDisableUser
   * @description Toggles the activation status of the application.
   * @param {object} payload - The payload containing the updated application data.
   * @param {string} id - The ID of the application to be updated.
   * @param {number} orgName - The ID of the organization to which the application belongs.
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtring the data.
   * @returns {void} - updates the activation status of an application by making an API call
   * @example handleDisableUser(payload, applicationId, orgName)
   */

  const handleDisableSubadmin = useCallback(
    (currentUserDetail) => {
      const payload = {
        is_active: !currentUserDetail?.is_active,
      };
      updateSubadmin(currentUserDetail?.id, payload).then(() => {
        getUserList(0, pageSize, searchTerm).then((resp) => {
          setUserList(resp?.data);
          setPageCount(resp?.max_pages);
          setTotalCount(resp?.total_count);
        });
        setShowStatusChangeModal(false);
      });
    },
    [
      updateSubadmin,
      getUserList,
      pageSize,
      searchTerm,
      setUserList,
      setPageCount,
      setTotalCount,
      setShowStatusChangeModal,
    ]
  );
  /**
   * @function onTableStateChangeMethod
   * @description helps in setting the pageIndex and pageSize
   * @param {object} pageIndex - pageIndex method of the table.
   * @returns {void} - updates the pageIndex and pageSize.
   * @example onTableStateChangeMethod(pageIndex)
   */
  const onTableStateChangeMethod = useCallback(
    (pageIndexData) => {
      setPageIndex(pageIndexData?.pageIndex);
      setPageSize(pageIndexData?.pageSize);
    },
    [setPageIndex, setPageSize]
  );

  const menus = useMemo(
    () => [
      {
        label: 'Edit',
        id: 'edit',
        icon: EditIcon,
        onClick: ({ props }) => {
          setEditUser(props?.original);
          setMode('edit');
          setOpen(true);
        },
      },
      {
        label: 'Delete',
        id: 'delete',
        icon: DeleteIcon,
        onClick: ({ props }) => {
          setShowDeleteModal(true);
          setDeleteUser(props?.original);
        },
      },
      {
        label: 'Change Status',
        id: 'disable',
        icon: ChangeCircleIcon,
        onClick: ({ props }) => {
          setShowStatusChangeModal(true);
          setDeleteUser(props?.original);
        },
      },
    ],
    [pageSize, pageIndex, searchTerm]
  );
  /**
   * @function handleDeleteUser()
   * @description This function helps in deleting the application
   * @param {number} orgName - The ID of the organization to delete.
   * @param {number} pageIndex - The page page index of current page.
   * @param {number} pageSize - The page size of current page.
   * @param {number} searchTerm - search term for filtring the data.
   * @returns {void}
   * @example
   */
  const handleDeleteUser = useCallback(
    (id, orgId, pageIndexData, pageSizeData, searchTermData) => {
      deteleSubadmin(id).then(() => {
        getUserList(pageIndexData, pageSizeData, searchTermData).then(
          (resp) => {
            setUserList(resp?.data);
            setPageCount(resp?.max_pages);
            setTotalCount(resp?.total_count);
          }
        );
      });
      setShowDeleteModal(false);
    },
    [deteleSubadmin, getUserList, setShowDeleteModal, setUserList]
  );

  return (
    <Grid container padding="20px">
      <Grid item xs={12}>
        <Box display="flex" flexDirection="column" overflow="auto">
          <Typography variant="h3">Sub-Admins</Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="flex-end"
          mt={2}
        >
          <Box width="200px">
            <SearchBox
              placeholder="Search"
              variant="filled"
              value={searchQueryText}
              onChange={handleSearchUserByName}
              onInput={(e) => handleSetSearchText(e.target.value)}
              aria-label="Search sub-admin"
            />
          </Box>
          <Button
            title="Create Sub-Admin"
            variant="contained"
            onClick={() => setOpen(true)}
          >
            + Create Sub-Admin
          </Button>
        </Box>
        <Grid item xs={12} style={{ flexGrow: 1 }}>
          {isLoading ? (
            <Box
              flexGrow={1}
              display="flex"
              justifyContent="center"
              alignItems="center"
              flexDirection="column"
              height="50vh"
            >
              <Typography variant="h6" textAlign="center" sx={{ pt: '10px' }}>
                Loading...
              </Typography>
            </Box>
          ) : (
            <Slide in={true} direction="up" mountOnEnter timeout={500}>
              {userList?.length > 0 ? (
                <Box pt={2} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <ReactMuiTableListView
                    data={userList}
                    columns={tableColumns}
                    onTableStateChange={onTableStateChangeMethod}
                    getHeaderProps={() => ({
                      style: {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    })}
                    getRowProps={() => ({
                      style: {
                        position: 'relative',
                      },
                    })}
                    enableRowSelection={true}
                    pageCount={Number(pageCount)}
                    dataLength={Number(totalCount)}
                    enablePagination={true}
                    initialPageSize={5}
                    rowsPerPageOptions={[5, 10, 15, 20]}
                    rowLevelRightClickMenus={menus}
                    enableManualFilter={true}
                    enableControlledPagination={true}
                    showPaginationAction={false}
                    skipPageReset={true}
                    searchTerm={searchTerm}
                  />
                </Box>
              ) : (
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexDirection="column"
                  overflow="auto"
                  minHeight="300px"
                  height="calc(100vh - 438px)"
                >
                  {searchTerm ? (
                    <NoDataImage
                      style={{ color: theme.palette.primary.main }}
                    />
                  ) : (
                    <NoDataCreated
                      style={{ color: theme.palette.primary.main }}
                    />
                  )}
                  <Typography
                    variant="subtitle1"
                    textAlign="center"
                    sx={{ pt: '10px' }}
                  >
                    {searchTerm
                      ? 'No matching result found.'
                      : 'No sub-admins found. Please create a sub-admin to proceed.'}
                  </Typography>
                </Box>
              )}
            </Slide>
          )}
        </Grid>

        <DeleteDialog
          open={showDeleteModal}
          onCloseClick={() => setShowDeleteModal(false)}
          onDeleteClick={() =>
            handleDeleteUser(
              deleteUser?.id,
              orgName,
              pageIndex,
              pageSize,
              searchTerm
            )
          }
          confirmationMsg={`Are you sure you want to delete "${deleteUser?.full_name}" Sub-admin ?`}
          resourceName="Sub-admin"
        />
        <ChangeStatusDialog
          open={showStatusChangeModal}
          onCloseClick={() => setShowStatusChangeModal(false)}
          onChangeClick={() => handleDisableSubadmin(deleteUser)}
          confirmationMsg={`Are you sure you want to change the status of "${deleteUser?.full_name}" to "${deleteUser?.is_active ? 'Inactive' : 'Active'}"?`}
        />
        <AddSubadminTabDialog
          open={open}
          setOpen={setOpen}
          mode={mode}
          currentUserDetail={editUser}
          setMode={setMode}
          orgName={orgName}
          setUserList={setUserList}
          pageIndex={pageIndex}
          pageSize={pageSize}
          searchTerm={searchTerm}
          setPageCount={setPageCount}
          setTotalCount={setTotalCount}
        />
      </Grid>
    </Grid>
  );
};

export default withTransaction('Subadmin', 'component')(Subadmin);
